import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../state/hooks";
import { closeConfirmationModal } from "../../state/slice/modal/modalSlice";
import ModalStandard from "./ModalStandard";

type ModalConfirmDangerousActionProps = {
  id: string;
  title: string;
  itemName: string;
  warningMessages: string[];
  consequencesList: string[];
  onConfirm: () => void;
  onCancel?: () => void;
};

const ModalConfirmDangerousAction = ({
  id,
  title,
  itemName,
  warningMessages,
  consequencesList,
  onConfirm,
  onCancel,
}: ModalConfirmDangerousActionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState("");
  const isConfirmEnabled = inputValue === itemName;

  const handleCancel = () => {
    setInputValue("");
    if (onCancel) {
      onCancel();
    }
    dispatch(closeConfirmationModal(id));
  };

  const handleConfirm = () => {
    if (isConfirmEnabled) {
      setInputValue("");
      onConfirm();
      dispatch(closeConfirmationModal(id));
    }
  };

  return (
    <ModalStandard id={id} fullScreen={false} modalType={"confirmation"}>
      <div className="modalContent personalize-scroll-visible">
        <div>
          {/* Title */}
          <h2 className="font-bold text-2xl mb-6 text-gray-900">{title}</h2>

          {/* Warning Messages */}
          <div className="mb-6 space-y-3">
            {warningMessages.map((warning, index) => (
              <div
                key={index}
                className="bg-red-100 rounded-lg p-4 text-red-700 font-medium"
              >
                {warning}
              </div>
            ))}
          </div>

          {/* Consequences List */}
          {consequencesList.length > 0 && (
            <div className="mb-6">
              <p className="font-semibold text-gray-900 mb-2">
                {t("modal.deletePublishedProject.consequences")}
              </p>
              <ul className="list-none space-y-1 text-gray-700">
                {consequencesList.map((consequence, index) => (
                  <li key={index}>{consequence}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Type to Confirm */}
          <div className="mb-6">
            <p className="font-semibold text-gray-900 mb-2">
              {t("modal.deletePublishedProject.typeToConfirm")}
            </p>
            <p className="text-sm text-gray-600 mb-3 bg-gray-100 p-2 rounded">
              {itemName}
            </p>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 ${
                inputValue && !isConfirmEnabled
                  ? "focus:!ring-red-500 focus:!border-red-500"
                  : ""
              }`}
              placeholder={t("modal.deletePublishedProject.typePlaceholder")}
              autoFocus
            />
            <p
              className={`text-sm text-red-500 mt-2 ${
                inputValue && !isConfirmEnabled ? "" : "invisible"
              }`}
            >
              {t("modal.deletePublishedProject.nameDoesNotMatch")}
            </p>
          </div>

          {/* Final Confirmation Question */}
          <p className="font-semibold text-gray-900 text-center mb-4">
            {t("modal.deletePublishedProject.areYouAbsolutelySure")}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end mt-4 space-x-3">
        <button type="button" className="btn-alternative-outline" onClick={handleCancel}>
          {t("general.cancel")}
        </button>
        <button
          type="button"
          className={`px-6 py-2 rounded-lg font-medium transition-all ${
            isConfirmEnabled
              ? "btn-danger-fill cursor-pointer"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
        >
          {t("modal.deletePublishedProject.confirmButton")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default ModalConfirmDangerousAction;
